// Offer to send the customer-facing reference document on a completed training session (Session
// AFP, Stream B1). The moment a session is completed, if a customer-facing reference document is on
// file for that training type, the office is offered a one-click send to the customer (pre-filled
// from the site's customer contact). If none is on file, nothing is shown and nothing is blocked:
// completion never depends on a reference document existing.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import type { TrainingReferenceDocumentDto, TrainingType } from "../../lib/trainingTypes";
import { SendTrainingDocModal } from "./SendTrainingDocModal";
import { Button } from "../../components/Button";

export function TrainingSessionReferenceSend({ trainingType, siteId }: { trainingType: TrainingType; siteId: string }) {
  const [doc, setDoc] = useState<TrainingReferenceDocumentDto | null>(null);
  const [open, setOpen] = useState(false);
  const [sentMsg, setSentMsg] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    // Promise.resolve().then(...) so a partially-mocked api or any hiccup can never break the page.
    Promise.resolve()
      .then(() => api.trainingDocuments.list({ trainingType }))
      .then((r) => {
        if (!live) return;
        // The list already returns current (not superseded, not deleted) documents; pick the
        // customer-facing one for this type.
        setDoc(r.documents.find((d) => d.customerFacing && !d.superseded) ?? null);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [trainingType]);

  if (!doc) return null; // no reference document on file: no prompt, and completion is never blocked

  return (
    <div className="rounded-ele border border-ele-green/50 bg-ele-surface p-5">
      <h2 className="mb-2 font-heading text-lg text-ele-green">Reference document for the customer</h2>
      {sentMsg ? (
        <p role="status" className="text-sm text-ele-strong">
          {sentMsg}
        </p>
      ) : (
        <>
          <p className="text-sm text-ele-body">
            A customer-facing reference document is on file for this training. Send it to the customer so their staff
            can follow the procedure on site.
          </p>
          <p className="mt-1 text-sm text-ele-strong">
            {doc.title}
            {doc.version ? ` (${doc.version})` : ""}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button size="sm" onClick={() => setOpen(true)}>
              Send reference document to the customer
            </Button>
            <Link to="/office/training-documents" className="text-sm text-ele-green hover:underline">
              Manage reference documents
            </Link>
          </div>
        </>
      )}
      {open && (
        <SendTrainingDocModal
          doc={doc}
          siteId={siteId}
          onClose={() => setOpen(false)}
          onSent={(m) => {
            setOpen(false);
            setSentMsg(m);
          }}
        />
      )}
    </div>
  );
}
